
// ...existing code...

// Place this after all variables (profileAudioEnabled, finished, playCallAudio, etc.) are defined inside BingoGame
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import useGameBase from "../../hooks/useGameBase";
import SocketContext from "../../context/SocketContext";
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";

export default function BingoGame({
  roomId,
  gameState,
  gamePlayers = [],
  user = {},
  isSpectator = false,
}) {
  const socket = useContext(SocketContext);
  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return (gamePlayers || []).find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [gamePlayers, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }

    if (!Array.isArray(gamePlayers) || gamePlayers.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }

    const exists = gamePlayers.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });

    if (!exists) {
      const first = gamePlayers[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [gamePlayers, isSpectator, spectatorViewPlayerId]);

  const { submitAction, perspectiveUser, perspectiveIds } = useGameBase({
    gameState,
    gamePlayers,
    user,
    roomId,
    socket,
    perspectivePlayer,
  });
  const perspectiveSocketId = perspectiveUser?.socketId || null;
  const perspectiveUserId = perspectiveUser?.userId || null;

  const findPerspectiveValue = useCallback((source) => {
    if (!source || typeof source !== "object") return null;
    for (const key of perspectiveIds) {
      if (typeof key !== "undefined" && key !== null && source[key]) {
        return source[key];
      }
    }
    return null;
  }, [perspectiveIds]);

  // Try to locate player's board by socket id first, then by userId if available
  let myBoardObj = null;
  if (gameState && gameState.perPlayerBoards) {
    myBoardObj = findPerspectiveValue(gameState.perPlayerBoards) || null;
    if (!myBoardObj && perspectiveUserId) {
      myBoardObj = gameState.perPlayerBoards[perspectiveUserId] || myBoardObj;
    }
    // As a last resort, if there's only one board and it's keyed differently, try to find an entry matching username
    if (!myBoardObj) {
      const boardsEntries = Object.entries(gameState.perPlayerBoards || {});
      const found = boardsEntries.find(
        ([, v]) => v && v.username && v.username === perspectiveUser?.username
      );
      if (found) myBoardObj = found[1];
    }
  }

  // Track how many lines the player has completed
  const board = myBoardObj ? myBoardObj.board : [];
  const marks = myBoardObj ? myBoardObj.marks : [];
  const [optimisticMarks, setOptimisticMarks] = useState(() => new Set());
  const [rejectedMarks, setRejectedMarks] = useState(() => new Set());
  const gameStateCalledNumbers =
    gameState && gameState.calledNumbers ? gameState.calledNumbers : null;
  const called = useMemo(
    () =>
      gameStateCalledNumbers && Array.isArray(gameStateCalledNumbers)
        ? gameStateCalledNumbers
        : [],
    [gameStateCalledNumbers]
  );
  const currentCall =
    gameState &&
    (gameState.currentCall ||
      (called.length > 0 ? called[called.length - 1] : null));
  const finished = !!gameState?.finished;
  const boardCols =
    gameState?.boardCols || (board && board[0] ? board[0].length : 0);
  // const balls = gameState?.balls || 75; // not used directly in this component

  // CALL_DESCRIPTIONS is defined at module scope
  // build a quick set of winning coords for highlighting

  // Config: Highlight Calls (default true)
  // Always use the latest config value for highlightCalls (default true)
  const highlightCalls = Boolean(
    gameState && gameState.config &&
    (typeof gameState.config.highlightCalls === 'boolean'
      ? gameState.config.highlightCalls
      : true)
  );



  // --- Place this block after profileAudioEnabled and playCallAudio are defined ---



    // --- Place after playCallAudio/profileAudioEnabled (after line 220) ---
  const winningCoordsSet = new Set();
  if (gameState && Array.isArray(gameState.winningLines)) {
    for (const wl of gameState.winningLines) {
      if (wl && Array.isArray(wl.coords)) {
        for (const [rr, cc] of wl.coords) winningCoordsSet.add(`${rr}:${cc}`);
      }
    }
  }

  const markCell = async (r, c) => {
    try {
      // optimistic UI: reflect the mark immediately
      const key = `${r}:${c}`;
      try {
        setOptimisticMarks((s) => {
          const n = new Set(Array.from(s));
          n.add(key);
          return n;
        });
      } catch (e) {}
      const action = { type: "markCell", row: r, col: c };
      const ack = await submitAction(action);
      // debug: log socket and ack
      try {
        console.debug("markCell", {
          socketId: socket?.id,
          perspectiveSocketId,
          roomId,
          action,
          ack,
        });
      } catch (e) {}
      // If server rejected the action, remove optimistic mark and optionally show a brief rejection
      const rejected =
        ack &&
        (ack.ok === false ||
          ack.error ||
          (ack.status && String(ack.status).startsWith("ignored")));
      if (rejected) {
        try {
          setOptimisticMarks((s) => {
            const n = new Set(Array.from(s));
            n.delete(key);
            return n;
          });
        } catch (e) {}
        // show a brief rejected visual indicator
        try {
          setRejectedMarks((s) => {
            const n = new Set(Array.from(s));
            n.add(key);
            return n;
          });
          setTimeout(() => {
            setRejectedMarks((s) => {
              const n = new Set(Array.from(s));
              n.delete(key);
              return n;
            });
          }, 1200);
        } catch (e) {}
      }
    } catch (e) {
      console.error("markCell failed", e);
    }
  };

  // reconcile optimistic marks with server-confirmed marks: remove any optimistic entries that are now confirmed
  const myMarks = myBoardObj ? myBoardObj.marks : null;
  useEffect(() => {
    try {
      if (!myMarks || !Array.isArray(myMarks)) return;
      const keep = new Set();
      for (const key of Array.from(optimisticMarks)) {
        const [rs, cs] = key.split(":").map((x) => Number(x));
        if (!isNaN(rs) && !isNaN(cs)) {
          const confirmed = myMarks && myMarks[rs] && myMarks[rs][cs];
          if (!confirmed) keep.add(key);
        }
      }
      // Only update if different
      const same =
        keep.size === optimisticMarks.size &&
        Array.from(keep).every((k) => optimisticMarks.has(k));
      if (!same) setOptimisticMarks(keep);
    } catch (e) {
      /* ignore */
    }
  }, [myMarks, optimisticMarks]);

  const lastPlayedCallRef = useRef(null);
  const bingoCallAudioRef = useRef(null);
  const eyesDownAudioRef = useRef(null);
  const pendingCallAudioFileRef = useRef(null);
  const pendingEyesDownRef = useRef(false);
  const [eyesDownVisible, setEyesDownVisible] = useState(false);
  const startedRef = useRef(false);
  const [callAnimated, setCallAnimated] = useState(false);
  // circular timer control: interval is provided by game config/state; fall back to 10s
  const callIntervalSeconds =
    gameState?.config?.intervalSeconds || gameState?.intervalSeconds || 10;
  const [timerKey, setTimerKey] = useState(0);
  const profileAudioEnabled = !!(perspectiveUser && perspectiveUser.audioEnabled);
  const prevCallRef = useRef(null);

  const playCallAudio = useCallback((fileName) => {
    if (!profileAudioEnabled || !bingoCallAudioRef.current || !fileName) return;
    try {
      bingoCallAudioRef.current.pause();
      bingoCallAudioRef.current.currentTime = 0;
      bingoCallAudioRef.current.src = `/sounds/bingo/${fileName}`;
      bingoCallAudioRef.current.load();
      const playAttempt = bingoCallAudioRef.current.play();
      if (playAttempt && typeof playAttempt.catch === "function") {
        playAttempt
          .then(() => {
            if (pendingCallAudioFileRef.current === fileName) {
              pendingCallAudioFileRef.current = null;
            }
          })
          .catch(() => {
            pendingCallAudioFileRef.current = fileName;
          });
      } else {
        pendingCallAudioFileRef.current = null;
      }
    } catch (e) {
      pendingCallAudioFileRef.current = fileName;
    }
  }, [profileAudioEnabled]);

  const playEyesDownAudio = useCallback(() => {
    if (!profileAudioEnabled || !eyesDownAudioRef.current) return;
    try {
      eyesDownAudioRef.current.pause();
      eyesDownAudioRef.current.currentTime = 0;
      eyesDownAudioRef.current.src = "/sounds/bingo/eyesdown.mp3";
      eyesDownAudioRef.current.load();
      eyesDownAudioRef.current.muted = false;
      eyesDownAudioRef.current.volume = 1;
      const playAttempt = eyesDownAudioRef.current.play();
      if (playAttempt && typeof playAttempt.catch === "function") {
        playAttempt
          .then(() => {
            pendingEyesDownRef.current = false;
          })
          .catch(() => {
            pendingEyesDownRef.current = true;
          });
      } else {
        pendingEyesDownRef.current = false;
      }
    } catch (e) {
      pendingEyesDownRef.current = true;
    }
  }, [profileAudioEnabled]);

  useEffect(() => {
    if (!profileAudioEnabled) {
      pendingCallAudioFileRef.current = null;
      pendingEyesDownRef.current = false;
      return;
    }

    const retryPendingAudio = () => {
      if (pendingEyesDownRef.current) {
        playEyesDownAudio();
        return;
      }
      const pendingFile = pendingCallAudioFileRef.current;
      if (!pendingFile || !bingoCallAudioRef.current) return;
      pendingCallAudioFileRef.current = null;
      try {
        bingoCallAudioRef.current.pause();
        bingoCallAudioRef.current.currentTime = 0;
        bingoCallAudioRef.current.src = `/sounds/bingo/${pendingFile}`;
        bingoCallAudioRef.current.play().catch(() => {
          pendingCallAudioFileRef.current = pendingFile;
        });
      } catch (e) {
        pendingCallAudioFileRef.current = pendingFile;
      }
    };

    window.addEventListener("pointerdown", retryPendingAudio, true);
    window.addEventListener("keydown", retryPendingAudio, true);
    return () => {
      window.removeEventListener("pointerdown", retryPendingAudio, true);
      window.removeEventListener("keydown", retryPendingAudio, true);
    };
  }, [playEyesDownAudio, profileAudioEnabled]);

  useEffect(() => {
    if (!profileAudioEnabled) return;
    const num = currentCall;
    if (num == null) return;
    if (lastPlayedCallRef.current === num) return;
    lastPlayedCallRef.current = num;

    playCallAudio(`${num}.mp3`);
  }, [currentCall, profileAudioEnabled, playCallAudio]);

  useEffect(() => {
    const callAudioEl = bingoCallAudioRef.current;
    const eyesDownEl = eyesDownAudioRef.current;
    return () => {
      if (callAudioEl) {
        try {
          callAudioEl.pause();
          callAudioEl.currentTime = 0;
        } catch (e) {}
      }
      if (eyesDownEl) {
        try {
          eyesDownEl.pause();
          eyesDownEl.currentTime = 0;
        } catch (e) {}
      }
    };
  }, []);

  // trigger animation when currentCall changes
  useEffect(() => {
    const num = currentCall;
    if (num == null) return;
    if (prevCallRef.current === num) return;
    prevCallRef.current = num;
    // toggle animation flag briefly
    setCallAnimated(false);
    // small timeout to allow reflow and restart animation
    setTimeout(() => setCallAnimated(true), 20);
    // restart circular timer animation by bumping the key
    try {
      setTimerKey((k) => k + 1);
    } catch (e) {}
    // keep pulsing state true; UI CSS will handle continuous pulse
  }, [currentCall]);


  // announce and show 'Eyes Down!' when game starts
  const started = !!gameState?.started;
  useEffect(() => {
    // only show Eyes Down if the game has just started and no numbers have been called yet
    const noNumbersCalled =
      !called || (Array.isArray(called) && called.length === 0);
    if (!startedRef.current && started && noNumbersCalled) {
      // game transitioned to started
      startedRef.current = true;
      setEyesDownVisible(true);
      playEyesDownAudio();
      setTimeout(() => {
        if (pendingEyesDownRef.current) {
          playEyesDownAudio();
        }
      }, 400);
      // hide overlay after 2.5s
      setTimeout(() => setEyesDownVisible(false), 2500);
    } else if (!started) {
      // reset for next game
      startedRef.current = false;
    }
  }, [started, called, profileAudioEnabled, playEyesDownAudio]);

  const boardVariantLabel =
    gameState?.balls === 90
      ? "90 Ball"
      : gameState?.balls === 80
      ? "80 Ball"
      : "75 Ball";
  const calledCount = Array.isArray(called) ? called.length : 0;
  const playerMarkedNumbers =
    gameState &&
    gameState.perPlayerMarkedNumbers &&
    findPerspectiveValue(gameState.perPlayerMarkedNumbers) &&
    Array.isArray(findPerspectiveValue(gameState.perPlayerMarkedNumbers))
      ? findPerspectiveValue(gameState.perPlayerMarkedNumbers)
      : [];
  const markedCount = Array.isArray(board)
    ? board.reduce(
        (total, row, r) =>
          total +
          (Array.isArray(row)
            ? row.reduce((rowTotal, num, c) => {
                if (num === null || typeof num === "undefined") {
                  return rowTotal;
                }
                const key = `${r}:${c}`;
                const isActuallyMarked = !!(
                  (marks && marks[r] && marks[r][c]) ||
                  playerMarkedNumbers.includes(num) ||
                  (optimisticMarks && optimisticMarks.has && optimisticMarks.has(key))
                );
                return rowTotal + (isActuallyMarked ? 1 : 0);
              }, 0)
            : 0),
        0
      )
    : 0;

  // Play 1st line, 2nd line, and full house audio cues robustly

  // Play 1st line audio only once, after the number is called
  useEffect(() => {
    if (!profileAudioEnabled) return;
    const award = gameState?.firstLine;
    if (award && Array.isArray(award.sids) && award.sids.length > 0 && award.call != null) {
      // Only play if this is the first time for this award and the current call matches the award call
      if (
        (!window.__bingoFirstLinePlayed || window.__bingoFirstLinePlayed !== award.call) &&
        currentCall === award.call
      ) {
        playCallAudio("1stline.mp3");
        window.__bingoFirstLinePlayed = award.call;
      }
    }
  }, [gameState?.firstLine, currentCall, profileAudioEnabled, playCallAudio]);

  // Play 2nd line audio only once, after the number is called
  useEffect(() => {
    if (!profileAudioEnabled) return;
    const award = gameState?.secondLine;
    if (award && Array.isArray(award.sids) && award.sids.length > 0 && award.call != null) {
      if (
        (!window.__bingoSecondLinePlayed || window.__bingoSecondLinePlayed !== award.call) &&
        currentCall === award.call
      ) {
        playCallAudio("2ndline.mp3");
        window.__bingoSecondLinePlayed = award.call;
      }
    }
  }, [gameState?.secondLine, currentCall, profileAudioEnabled, playCallAudio]);

  useEffect(() => {
    if (!profileAudioEnabled) return;
    if (gameState?.finished) {
      if (!window.__bingoFullHousePlayed || window.__bingoFullHousePlayed !== gameState.finishedEpoch) {
        playCallAudio("fullhouse.mp3");
        window.__bingoFullHousePlayed = gameState.finishedEpoch;
      }
    }
  }, [gameState?.finished, gameState?.finishedEpoch, profileAudioEnabled, playCallAudio]);

  return (
    <div className="p-3 w-full max-w-3xl relative">
      {isSpectator && gamePlayers.length > 0 && (
        <SpectatorPerspectiveSelector
          players={gamePlayers}
          value={spectatorViewPlayerId}
          onChange={setSpectatorViewPlayerId}
        />
      )}

      <audio ref={bingoCallAudioRef} preload="auto" playsInline style={{ display: "none" }} />
      <audio ref={eyesDownAudioRef} preload="auto" playsInline style={{ display: "none" }} />
      {eyesDownVisible && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-[1000]">
          <div className="text-4xl font-extrabold text-white p-6 rounded">
            Eyes Down!
          </div>
        </div>
      )}
      <div className="mb-7 text-center">
        <div
          className={`mx-auto inline-flex items-center justify-center w-48 h-24 rounded ${
            callAnimated ? "animate-bingo-pop" : ""
          } dark:text-gray-300 text-gray-600`}
        >
          {/* make the timer square so the number is centered */}
          <div
            className="relative flex items-center justify-center"
            style={{ width: 120, height: 120 }}
          >
            {/* Circular timer SVG positioned behind the number */}
            <svg
              key={`timer-${timerKey}`}
              className="absolute inset-0"
              width="120"
              height="120"
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <defs>
                <linearGradient id={`timerGrad-${timerKey}`} x1="0%" x2="100%">
                  <stop offset="0%" stopColor="#0606f1ff" />
                  <stop offset="100%" stopColor="#0ac1e1ff" />
                </linearGradient>
              </defs>
              <circle
                cx="50"
                cy="50"
                r="44"
                stroke="#e5e7eb"
                strokeWidth="8"
                fill="none"
              />
              {
                // animated circle: use CSS variable --circ to hold circumference so keyframes can reference it
              }
              <circle
                cx="50"
                cy="50"
                r="44"
                stroke={`url(#timerGrad-${timerKey})`}
                strokeWidth="8"
                strokeLinecap="round"
                fill="none"
                style={{
                  transform: "rotate(-90deg)",
                  transformOrigin: "50% 50%",
                  // circumference = 2 * PI * r
                  "--circ": `${2 * Math.PI * 44}`,
                  strokeDasharray: `${2 * Math.PI * 44}`,
                  strokeDashoffset: "var(--circ)",
                  animation: `timer-progress ${callIntervalSeconds}s linear forwards`,
                }}
              />
            </svg>

            <div
              className={`text-5xl font-extrabold ${
                callAnimated ? "animate-bingo-pulse" : ""
              }`}
            >
              {currentCall ?? ""}
            </div>
          </div>
        </div>
      </div>

      <style>
        {`@keyframes bingoPop { 0% { transform: scale(0.4); opacity: 0 } 60% { transform: scale(1.05); opacity: 1 } 100% { transform: scale(1); } }
          @keyframes bingoPulse { 0% { transform: scale(1); } 50% { transform: scale(1.06); } 100% { transform: scale(1); } }
          .animate-bingo-pop { animation: bingoPop 360ms ease-out both; dark:text-gray-300; text:gray-600;  }
          .animate-bingo-pulse { animation: bingoPulse 1400ms ease-in-out 360ms infinite;  dark:text-gray-300; text:gray-600; }
          @keyframes timer-progress { from { stroke-dashoffset: var(--circ); } to { stroke-dashoffset: 0; } }
          svg[aria-hidden] { pointer-events: none; }
        `}
      </style>
      <div className="mb-4 rounded-[24px] border border-sky-200/70 bg-white p-3 shadow-[0_16px_40px_rgba(14,116,144,0.14)] dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.35em] text-sky-700 dark:text-sky-300">
              Your Board
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
            <span className="rounded-full border border-sky-300/80 bg-white/85 px-2.5 py-1 text-sky-700 shadow-sm dark:border-sky-800 dark:bg-slate-900/70 dark:text-sky-200">
              {boardVariantLabel}
            </span>
            <span className="rounded-full border border-emerald-300/80 bg-emerald-50/90 px-2.5 py-1 text-emerald-700 shadow-sm dark:border-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200">
              {markedCount} marked
            </span>
            <span className="rounded-full border border-amber-300/80 bg-amber-50/90 px-2.5 py-1 text-amber-700 shadow-sm dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
              {calledCount} called
            </span>
          </div>
        </div>

      <div className="grid gap-2 justify-center">
        {board && board.length > 0 ? (
          (() => {
            // choose a cell size based on number of columns so wide boards fit
            // Reduce cell size for 90-ball games for better mobile fit
            let cellSize;
            if (gameState?.balls === 90) {
              // Reduce by about 1cm (~10px)
              cellSize = boardCols >= 9 ? 30 : 34;
            } else {
              cellSize =
                boardCols >= 9
                  ? 34
                  : boardCols === 4
                  ? 50
                  : boardCols === 5
                  ? 56
                  : 44;
            }
            const fontSize = Math.max(12, Math.floor(cellSize * 0.36));
            const cellStyle = {
              width: `${cellSize}px`,
              height: `${cellSize}px`,
              fontSize: `${fontSize}px`,
            };
            const boardFrameStyle = {
              gridTemplateColumns: `repeat(${boardCols}, ${cellSize}px)`,
              gap: boardCols >= 9 ? 3 : 5,
            };
            return (
              <div
                className="grid rounded-[22px] border border-white/70 bg-white p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_12px_30px_rgba(15,23,42,0.12)] dark:border-slate-600 dark:bg-slate-900"
                style={boardFrameStyle}
              >
                {board.map((row, r) =>
                  row.map((num, c) => {
                    // skip rendering empty/null cells as blanks, except render the 'FREE' center cell when it's marked
                    if (num === null || typeof num === "undefined") {
                      const isFreeMarked = marks && marks[r] && marks[r][c];
                      if (isFreeMarked) {
                        // Render a visible FREE cell (center of 75-ball)
                        return (
                          <div
                            key={`${r}-${c}`}
                            style={cellStyle}
                            className="flex items-center justify-center rounded-xl border border-fuchsia-200 bg-[linear-gradient(135deg,_#fef3c7,_#fbcfe8)] px-1 text-[0.47rem] font-black uppercase tracking-[0.08em] text-fuchsia-700 shadow-[0_6px_14px_rgba(217,70,239,0.2)] dark:border-fuchsia-900 dark:bg-[linear-gradient(135deg,_rgba(146,64,14,0.85),_rgba(131,24,67,0.85))] dark:text-fuchsia-100"
                          >
                            FREE
                          </div>
                        );
                      }
                      return (
                        <div
                          key={`${r}-${c}`}
                          style={cellStyle}
                          className="rounded-xl border border-dashed border-slate-200/80 bg-transparent dark:border-slate-700/70"
                        />
                      );
                    }
                    const isMarked = marks && marks[r] && marks[r][c];
                    // also consider numbers previously marked for this player recorded in game state
                    const isMarkedFromState =
                      playerMarkedNumbers && playerMarkedNumbers.includes
                        ? playerMarkedNumbers.includes(num)
                        : false;
                    const key = `${r}:${c}`;
                    const isOptimistic =
                      optimisticMarks &&
                      optimisticMarks.has &&
                      optimisticMarks.has(key);
                    const effectiveMarked = !!(
                      isMarked ||
                      isOptimistic ||
                      isMarkedFromState
                    );
                    const isRejected =
                      rejectedMarks &&
                      rejectedMarks.has &&
                      rejectedMarks.has(key);
                    const isCalled = Array.isArray(called)
                      ? called.includes(num)
                      : false;
                    const isCallable =
                      !finished && !effectiveMarked && isCalled;
                    // determine confirmed vs optimistic marks for styling
                    const markedConfirmed = !!(isMarked || isMarkedFromState);
                    const markedOptimistic = !!(
                      isOptimistic && !markedConfirmed
                    );

                    let cellClass =
                      "relative flex items-center justify-center rounded-xl border p-0 font-extrabold transition-all duration-200 ease-out select-none ";
                    if (isRejected)
                      cellClass +=
                        "border-rose-300 bg-rose-500 text-white shadow-[0_8px_18px_rgba(244,63,94,0.28)]";
                    else if (markedConfirmed)
                      cellClass +=
                        "border-emerald-300 bg-[linear-gradient(145deg,_#34d399,_#059669)] text-white shadow-[0_10px_22px_rgba(5,150,105,0.34)]";
                    else if (markedOptimistic)
                      cellClass +=
                        "border-emerald-200 bg-[linear-gradient(145deg,_#bbf7d0,_#6ee7b7)] text-emerald-950 shadow-[0_8px_18px_rgba(16,185,129,0.24)]";

                    else {
                      // unmarked default state: add a subtle hover tint that respects light/dark themes
                      // If called but not marked, add a blue/gray background if highlightCalls is enabled
                      if (highlightCalls && isCalled) {
                        cellClass +=
                          " border-sky-300 bg-[linear-gradient(180deg,_rgba(56,189,248,0.22),_rgba(219,234,254,0.85))] text-slate-900 shadow-[0_8px_18px_rgba(56,189,248,0.18)] hover:-translate-y-[1px] hover:border-sky-400 hover:shadow-[0_12px_22px_rgba(56,189,248,0.22)] dark:border-sky-600 dark:bg-[linear-gradient(180deg,_rgba(30,64,175,0.38),_rgba(30,41,59,0.98))] dark:text-slate-100 dark:hover:border-sky-400 dark:hover:bg-[linear-gradient(180deg,_rgba(59,130,246,0.32),_rgba(30,41,59,0.98))]";
                      } else {
                        cellClass +=
                          " border-slate-200/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.95),_rgba(226,232,240,0.95))] text-slate-900 shadow-[0_8px_18px_rgba(148,163,184,0.16)] hover:-translate-y-[1px] hover:border-sky-300 hover:shadow-[0_12px_22px_rgba(56,189,248,0.16)] dark:border-slate-700 dark:bg-[linear-gradient(180deg,_rgba(51,65,85,0.98),_rgba(15,23,42,0.98))] dark:text-slate-100 dark:hover:border-sky-700 dark:hover:bg-[linear-gradient(180deg,_rgba(59,130,246,0.22),_rgba(30,41,59,0.98))]";
                      }
                    }

                    // Removed yellow border for winning squares

                    // show pointer when the cell is clickable
                    if (isCallable && !isSpectator) cellClass += " cursor-pointer";
                    else cellClass += " cursor-default";

                    return (
                      <button
                        key={`${r}-${c}`}
                        onClick={() => {
                          if (isCallable && !isSpectator) markCell(r, c);
                        }}
                        style={cellStyle}
                        className={cellClass}
                        title={
                          isMarked
                            ? "Marked"
                            : isOptimistic
                            ? "Marked (pending confirmation)"
                            : isRejected
                            ? "Rejected by server"
                            : !isCalled
                            ? "Number not called yet"
                            : isSpectator
                            ? "Spectators can view this board but cannot mark it"
                            : "Click to mark (only applied if number has been called)"
                        }
                      >
                        <span className="relative z-10">{num}</span>
                      </button>
                    );
                  })
                )}
              </div>
            );
          })()
        ) : (
          <div className="text-sm text-gray-500">No board</div>
        )}
      </div>
      </div>

      {/* First-line award message: show when server sets gameState.firstLine */}
      {gameState &&
      gameState.firstLine &&
      Array.isArray(gameState.firstLine.sids) &&
      gameState.firstLine.sids.length > 0
        ? (() => {
            const sids = gameState.firstLine.sids || [];
            const usernamesMap = gameState.firstLine.usernames || {};
            const names = sids
              .map((sid) => {
                return (
                  usernamesMap[sid] ||
                  (gameState.perPlayerBoards &&
                    gameState.perPlayerBoards[sid] &&
                    gameState.perPlayerBoards[sid].username) ||
                  sid
                );
              })
              .join(", ");
            return (
              <div className="mt-4 rounded-2xl border border-indigo-200 bg-[linear-gradient(135deg,_rgba(224,231,255,0.95),_rgba(199,210,254,0.9))] p-3 text-center text-sm font-semibold text-indigo-900 shadow-sm dark:border-indigo-800 dark:bg-[linear-gradient(135deg,_rgba(49,46,129,0.9),_rgba(67,56,202,0.55))] dark:text-indigo-50">
                First line completed by {names}
              </div>
            );
          })()
        : null}

      {/* Second-line award message: show when server sets gameState.secondLine */}
      {gameState &&
      gameState.secondLine &&
      Array.isArray(gameState.secondLine.sids) &&
      gameState.secondLine.sids.length > 0
        ? (() => {
            const sids = gameState.secondLine.sids || [];
            const usernamesMap = gameState.secondLine.usernames || {};
            const names = sids
              .map((sid) => {
                return (
                  usernamesMap[sid] ||
                  (gameState.perPlayerBoards &&
                    gameState.perPlayerBoards[sid] &&
                    gameState.perPlayerBoards[sid].username) ||
                  sid
                );
              })
              .join(", ");
            return (
              <div className="mt-4 rounded-2xl border border-yellow-200 bg-[linear-gradient(135deg,_rgba(254,243,199,0.95),_rgba(253,230,138,0.9))] p-3 text-center text-sm font-semibold text-yellow-900 shadow-sm dark:border-yellow-800 dark:bg-[linear-gradient(135deg,_rgba(202,138,4,0.9),_rgba(253,224,71,0.55))] dark:text-yellow-50">
                Second line completed by {names}
              </div>
            );
          })()
        : null}



      {gameState && gameState.perPlayerBoards
        ? (() => {
            const boardsMap = gameState.perPlayerBoards || {};
            const allSids = Object.keys(boardsMap).filter(
              (s) => s !== perspectiveSocketId
            );
            if (allSids.length === 0) return null;
            return (
              <div className="mt-6 rounded-[24px] border border-sky-200/70 bg-white p-4 shadow-[0_8px_24px_rgba(14,116,144,0.10)] dark:border-slate-700 dark:bg-slate-900">
                <div className="mb-3 text-xs font-bold uppercase tracking-[0.28em] text-slate-500 dark:text-slate-400">
                  Other Boards
                </div>
                <div className="flex gap-4 overflow-x-auto py-2">
                  {allSids.map((sid) => {
                    const entry = boardsMap[sid];
                    if (!entry || !entry.board) return null;
                    const miniBoard = entry.board;
                    const miniMarks = entry.marks || [];
                    const cols = miniBoard[0] ? miniBoard[0].length : 0;
                    const cell = 12; // small size
                    const miniStyle = {
                      display: "grid",
                      gridTemplateColumns: `repeat(${cols}, ${cell}px)`,
                      gap: 2,
                    };
                    const displayName = entry.username || sid;
                    return (
                      <div
                        key={`mini-${sid}`}
                        className="flex min-w-[92px] flex-col items-center rounded-2xl border border-slate-200/80 bg-slate-50/80 px-3 py-2 text-xs shadow-sm dark:border-slate-700 dark:bg-slate-950/50"
                      >
                        <div className="mb-2 w-20 truncate text-center text-[11px] font-semibold text-slate-700 dark:text-slate-300">
                          {displayName}
                        </div>
                        <div style={miniStyle} className="rounded-xl bg-transparent p-1">
                          {miniBoard.map((rarr, rr) =>
                            rarr.map((val, cc) => {
                              const marked =
                                miniMarks && miniMarks[rr] && miniMarks[rr][cc];
                              const empty = val === null || typeof val === "undefined";
                              const bg = marked
                                ? "bg-emerald-500 shadow-[0_3px_8px_rgba(16,185,129,0.35)]"
                                : empty
                                ? "bg-transparent"
                                : "bg-white dark:bg-gray-800";
                              return (
                                <div
                                  key={`${sid}-m-${rr}-${cc}`}
                                  style={{ width: cell, height: cell }}
                                  className={`${bg} rounded-[4px] border ${empty ? "border-dashed border-slate-200/60 dark:border-slate-700/60" : "border-gray-300 dark:border-gray-700"}`}
                                />
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()
        : null}

        
      {/* CALLED NUMBERS section styled like Other Boards */}
      {/* Only one CALLED NUMBERS section should exist, below Other Boards */}

      <section className="mt-6">
        <div
          className={
            `rounded-[24px] border border-sky-200/70 bg-white p-4 shadow-[0_8px_24px_rgba(14,116,144,0.10)] dark:border-slate-700 dark:bg-slate-900 mb-2 select-none`
          }
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-[0.28em] text-slate-700 dark:text-slate-200">
              CALLED NUMBERS
            </span>
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-300">
            {(called || []).join(", ")}
          </div>
        </div>
      </section>
      {/* Mini boards for other players (no numbers, only show green for their marked cells) */}

      
    </div>
  );
}

BingoGame.Guide = function BingoGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Bingo</h3>
      <p>
        Each player has a board of numbers. When a number is called, click it to
        mark it. First player to complete their full board wins. Special recognition for the first and second lines to be completed. 
      </p>
      <ul className="list-disc pl-5 mt-2 text-sm">
        <li>
          <strong>75-ball</strong>: 5x5 grid. The center is free and pre-marked.
        </li>
        <li>
          <strong>80-ball</strong>: 4x4 grid.
        </li>
        <li>
          <strong>90-ball</strong>: 3x9 grid with some empty slots — numbers in
          each column are ordered top-to-bottom.
        </li>
      </ul>
      
    </div>
  );
};
